import React, { useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { RotateCcw } from "lucide-react";

const ORD = [
  {
    q: "Build a RAG knowledge base in Bedrock from internal documents:",
    s: ["Create an S3 bucket and upload the documents", "Create and configure the knowledge base with S3 as its data source", "Ingest the data into the knowledge base"],
    w: "Build the container (bucket) → configure the thing that uses it (KB) → fill it (ingest).",
  },
  {
    q: "Run weekly batch predictions with SageMaker Pipelines, most cost-effectively:",
    s: ["Create a pipeline that runs batch inference", "Create an EventBridge role with StartPipelineExecution permission", "Create a scheduled EventBridge rule to run it weekly"],
    w: "Build → permit → trigger. (Batch, not a real-time endpoint idling all week; StartPipelineExecution, not CreatePipeline; scheduled rule, not event pattern.)",
  },
  {
    q: "Create and use features in SageMaker Feature Store:",
    s: ["Create a feature group", "Ingest the records", "Access the store to build training datasets"],
    w: "Build the container → fill it → use it. You cannot read records that were never written.",
  },
  {
    q: "Customize an LLM on Bedrock with a labeled dataset:",
    s: ["Prepare a dataset of labeled entries in JSON Lines format", "Run a fine-tuning job using a source model and the training data", "Deploy the fine-tuned model with provisioned throughput"],
    w: "Data first, then training, then serving: the model lifecycle in miniature.",
  },
];

function shuffle(arr) {
  return arr
    .map((v) => [Math.random(), v])
    .sort((a, b) => a[0] - b[0])
    .map((x) => x[1]);
}

export function OrderingDrill() {
  const [idx, setIdx] = useState(0);
  const [seed, setSeed] = useState(0);
  const [picked, setPicked] = useState([]);
  const [out, setOut] = useState("Click steps in order. Get it right and the next scenario loads.");

  const item = ORD[idx];
  const order = useMemo(() => shuffle(item.s.map((t, i) => ({ t, i }))), [idx, seed]);

  const next = useCallback(() => {
    setPicked([]);
    setIdx((v) => (v + 1) % ORD.length);
    setSeed((s) => s + 1);
    setOut("Click steps in order.");
  }, []);

  const pick = (origIdx) => {
    if (picked.includes(origIdx)) return;
    const np = [...picked, origIdx];
    setPicked(np);
    const wrong = np.some((v, i) => v !== i);
    if (wrong) {
      setOut("WRONG::" + item.w + " Hit reset and try again.");
      return;
    }
    if (np.length === item.s.length) {
      setOut("DONE::" + item.w + " Loading the next scenario…");
      setTimeout(next, 2200);
    } else {
      setOut(`Good, keep going (${np.length}/${item.s.length}).`);
    }
  };

  const status = out.startsWith("WRONG::") ? "wrong" : out.startsWith("DONE::") ? "done" : "info";
  const text = out.replace("WRONG::", "").replace("DONE::", "");

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="my-12 border-2 border-[#2F6F4F] bg-white/70 backdrop-blur-sm p-7 md:p-9"
      data-testid="widget-ordering-drill"
    >
      <div className="font-mono-ui text-[10px] font-bold uppercase tracking-[0.22em] text-[#2F6F4F] mb-6">
        Try it · Ordering drill — click the steps in the right order
      </div>
      <p className="text-[15px] font-semibold text-[#1C2430] mb-4">{item.q}</p>
      <div className="flex flex-col gap-2.5 mb-4">
        {order.map((o) => {
          const done = picked.includes(o.i);
          const rank = picked.indexOf(o.i);
          return (
            <motion.button
              key={o.i}
              onClick={() => pick(o.i)}
              whileTap={{ scale: 0.99 }}
              data-testid={`ordering-step-${o.i}`}
              className={`text-left flex items-center gap-3 px-4 py-3 border transition-colors duration-200 ${
                done ? "border-[#1C2430] bg-[#1C2430] text-[#FBFBF9]" : "border-[var(--border-heavy)] bg-white hover:bg-black/[0.03] cursor-pointer"
              }`}
            >
              <span className="font-mono-ui text-[11px] font-bold w-5 shrink-0">{done ? rank + 1 : "•"}</span>
              <span className="text-[14px]">{o.t}</span>
            </motion.button>
          );
        })}
      </div>
      <div
        className={`px-4 py-3 text-[13.5px] leading-relaxed bg-black/[0.03] ${status === "wrong" ? "text-[#D26040]" : status === "done" ? "text-[#2F6F4F]" : "text-[#1C2430]/75"}`}
        data-testid="ordering-output"
      >
        {status !== "info" && <strong>{status === "done" ? "Correct. " : "Not yet. "}</strong>}
        {text}
      </div>
      <button
        onClick={next}
        className="mt-4 font-mono-ui text-[11px] uppercase tracking-[0.12em] flex items-center gap-1.5 text-[#1C2430]/60 hover:text-[#1C2430] transition-colors"
        data-testid="ordering-reset"
      >
        <RotateCcw size={12} /> Reset / new scenario
      </button>
    </motion.div>
  );
}
